<script setup lang="ts">
import { Skeleton } from '@/components/ui/skeleton'
import useMyFeedback, {
	type CategoryFilter,
	type MyFeedback,
	type MyFeedbackCategory,
	type MyFeedbackStatusFamily,
	type StatusFilter,
} from '@/modules/users/composables/useMyFeedback'
import useMyFeedbackDetail from '@/modules/users/composables/useMyFeedbackDetail'
import {
	ArrowPathIcon,
	BugAntIcon,
	ChatBubbleLeftRightIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ClockIcon,
	GlobeAltIcon,
	LightBulbIcon,
} from '@heroicons/vue/24/outline'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'

const route = useRoute()

/**
 * El reporte al que apunta el deep link de la campana.
 *
 * Dos formas, y las dos siguen vivas (ENL-54): el segmento de
 * `/users/838/feedback/29`, que es lo que arma la campana hoy, y el
 * `?feedback=29` viejo, que sigue leyéndose porque hay campanas ya emitidas en
 * la base con ese formato.
 *
 * Se calcula ANTES de pedir la lista porque decide el tamaño de página: ver
 * `useMyFeedback`.
 */
const targetFeedbackId = computed<number | null>(() => {
	const raw = route.params.feedbackId ?? route.query.feedback
	const id = Number(raw)

	return Number.isInteger(id) && id > 0 ? id : null
})

const {
	feedbacks,
	meta,
	category,
	status,
	isLoading,
	isFetching,
	isError,
	setCategory,
	setStatus,
	setPage,
	refetch,
} = useMyFeedback({
	hasDeepLink: computed(() => targetFeedbackId.value !== null),
})

/**
 * Tarjeta abierta. Sólo una a la vez: la bitácora se pide al abrir, y permitir
 * varias abiertas convertiría la lista en N consultas simultáneas.
 */
const expandedId = ref<number | null>(null)

const {
	timeline,
	isLoading: isTimelineLoading,
	isError: isTimelineError,
	isExpired,
	isNotFound,
	reply,
	isReplying,
} = useMyFeedbackDetail(expandedId)

const toast = useToast()

/** Texto de la caja de respuesta de la tarjeta abierta. */
const replyBody = ref('')

function toggle(id: number) {
	expandedId.value = expandedId.value === id ? null : id
	replyBody.value = ''
}

/**
 * Responder en un reporte cerrado lo reabre, y eso hay que decirlo ANTES de que
 * el usuario escriba: enterarse después de mandar es enterarse tarde.
 */
async function submitReply(item: MyFeedback) {
	const body = replyBody.value.trim()

	if (!body) return

	// Se lee ANTES de mandar: al resolver, el reporte ya viene reabierto y
	// preguntarle entonces siempre contesta que no estaba cerrado. Es justo el
	// caso en que hay algo que avisarle al usuario.
	const wasClosed = item.isClosed

	try {
		await reply(body)
		replyBody.value = ''
		toast.success(
			wasClosed
				? 'Enviamos tu respuesta y reabrimos tu reporte.'
				: 'Enviamos tu respuesta.'
		)
	} catch (error) {
		const status = (error as { response?: { status?: number } })?.response?.status

		// El borrador NO se limpia en ninguna rama de error: el usuario ya
		// escribió su respuesta y perdérsela por un conflicto o un límite sería
		// castigarlo por algo que no hizo.
		if (status === 409) {
			// El seguimiento ya se repintó solo con lo que vino en el 409, así
			// que basta con decirle que mire antes de volver a enviar.
			toast.warning(
				'Tu reporte cambió mientras escribías. Revisa el seguimiento y vuelve a enviar si sigue aplicando.'
			)
			return
		}

		if (status === 429) {
			toast.error('Estás enviando muy seguido. Espera un momento y vuelve a intentarlo.')
			return
		}

		toast.error(
			status === 410
				? 'Este reporte ya cerró y dejó de mostrarse.'
				: 'No pudimos enviar tu respuesta. Vuelve a intentarlo.'
		)
	}
}

/**
 * Abre la tarjeta del deep link.
 *
 * `watch` con `immediate` y no `onMounted`: la campana puede navegar a OTRO
 * reporte del mismo perfil sin desmontar esta pestaña, y con `onMounted` el
 * segundo clic no abría nada. Es la misma clase de falla que ya dejó muerto
 * este enlace una vez.
 */
watch(
	targetFeedbackId,
	id => {
		if (id !== null) {
			expandedId.value = id
		}
	},
	{ immediate: true }
)

/**
 * El deep link apunta a un reporte que no está en la lista.
 *
 * Pasa cuando ya salió de la ventana de 48h: la campana no caduca junto con el
 * reporte, así que la liga sigue existiendo y no hay tarjeta que abrir. Sin
 * este aviso, el usuario llega desde una notificación y se queda mirando una
 * lista sin ninguna señal de por qué — que es exactamente lo que la issue pide
 * no dejar así.
 *
 * También cubre el caso de un filtro activo, y lo distingue: decirle «ya cerró»
 * a alguien que sólo tiene puesto el filtro de ideas seria mentirle.
 */
const targetIsMissing = computed(
	() =>
		targetFeedbackId.value !== null &&
		!isLoading.value &&
		!isError.value &&
		!feedbacks.value.some(item => item.id === targetFeedbackId.value)
)

const hasActiveFilter = computed(() => category.value !== null || status.value !== null)

/**
 * POR QUÉ no está, dicho con lo que contestó el servidor y no adivinando.
 *
 * La primera versión de este aviso decía «ya cerró y salió de la lista» en
 * cuanto no encontraba la tarjeta, y eso era mentira la mitad de las veces: el
 * reporte podía estar escondido por un filtro o en otra página. Decirle a
 * alguien que su reporte cerró cuando no cerró es peor que no decirle nada.
 *
 * El detalle ya se pidió —`expandedId` apunta al objetivo— así que la respuesta
 * de ese endpoint es la que sabe: 410 es la ventana de 48h, 404 es que no es
 * suyo, y un 200 significa que el reporte está vivo y simplemente no cayó en lo
 * que se está mostrando.
 */
const missingReason = computed<'filtered' | 'expired' | 'not-found' | 'elsewhere'>(() => {
	if (hasActiveFilter.value) return 'filtered'
	if (isExpired.value) return 'expired'
	if (isNotFound.value) return 'not-found'

	return 'elsewhere'
})

const categoryMeta: Record<
	MyFeedbackCategory,
	{
		label: string
		title: string
		icon: typeof BugAntIcon
		iconBg: string
		badgeClass: string
	}
> = {
	bug: {
		label: 'Problema',
		title: 'Reporte de Problema',
		icon: BugAntIcon,
		iconBg: 'bg-red-50 text-red-600 border border-red-100/80',
		badgeClass: 'bg-red-50 text-red-700 border-red-200/70',
	},
	idea: {
		label: 'Idea',
		title: 'Sugerencia de Idea',
		icon: LightBulbIcon,
		iconBg: 'bg-amber-50 text-amber-600 border border-amber-100/80',
		badgeClass: 'bg-amber-50 text-amber-700 border-amber-200/70',
	},
	otro: {
		label: 'Comentario',
		title: 'Comentario General',
		icon: ChatBubbleLeftRightIcon,
		iconBg: 'bg-blue-50 text-[#021F58] border border-blue-100/80',
		badgeClass: 'bg-blue-50 text-[#021F58] border-blue-200/70',
	},
}

const filters: { value: CategoryFilter; label: string; icon?: typeof BugAntIcon }[] = [
	{ value: null, label: 'Todos' },
	{ value: 'bug', label: 'Problemas', icon: BugAntIcon },
	{ value: 'idea', label: 'Ideas', icon: LightBulbIcon },
	{ value: 'otro', label: 'Comentarios', icon: ChatBubbleLeftRightIcon },
]

/**
 * Nueve estatus en píldoras no caben ni se leen. Un `select` además lo recorre
 * un lector de pantalla como un solo control, no como nueve botones.
 */
const statusFilters: { value: StatusFilter; label: string }[] = [
	{ value: null, label: 'Cualquier estatus' },
	{ value: 'nuevo', label: 'Recibido' },
	{ value: 'en_revision', label: 'En revisión' },
	{ value: 'confirmado', label: 'Confirmado' },
	{ value: 'en_progreso', label: 'En proceso de solución' },
	{ value: 'resuelto', label: 'Resuelto' },
	{ value: 'no_reproducible', label: 'No pudimos reproducirlo' },
	{ value: 'duplicado', label: 'Ya lo teníamos registrado' },
	{ value: 'descartado', label: 'Revisado, sin cambios' },
	{ value: 'planeada', label: 'Planeada' },
]

/**
 * El color sale de la familia, no del estatus: son tres estados de ánimo —sigue
 * vivo, se arregló, se cerró sin cambio— y el backend ya los resolvió.
 */
const familyBadge: Record<MyFeedbackStatusFamily, string> = {
	abierto: 'bg-blue-50 text-[#021F58] border-blue-200/70',
	resuelto: 'bg-emerald-50 text-emerald-700 border-emerald-200/70',
	cerrado: 'bg-gray-100 text-gray-600 border-gray-200/80',
}

const total = computed(() => meta.value?.total ?? 0)
const currentPage = computed(() => meta.value?.currentPage ?? 1)
const lastPage = computed(() => meta.value?.lastPage ?? 1)

function formatDateTime(value: string): string {
	return new Date(value).toLocaleString('es-MX', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}
</script>

<template>
	<div class="p-6 space-y-6">
		<!-- Section Header -->
		<div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
			<div>
				<div class="flex items-center gap-2">
					<h2 class="text-base font-semibold text-gray-900">Mis reportes y comentarios</h2>
					<span
						v-if="total > 0"
						class="px-2.5 py-0.5 text-xs font-semibold text-[#021F58] bg-blue-50 rounded-full border border-blue-100"
					>
						{{ total }}
					</span>
				</div>
				<p class="text-xs text-gray-500 mt-0.5">
					Historial de reportes e ideas enviadas a través del widget de feedback.
				</p>
				<!-- Sin esta línea la desaparición de un reporte cerrado se lee como
				     que se perdió. Es la explicación, no un detalle legal. -->
				<p class="text-[11px] text-gray-400 mt-1">
					Los reportes cerrados se muestran aquí 48 horas.
				</p>
			</div>

			<div class="flex flex-col gap-2 sm:items-end">
				<!-- Filter Pills -->
				<div class="flex flex-wrap items-center gap-1.5 bg-gray-100/70 p-1 rounded-xl">
				<button
					v-for="filter in filters"
					:key="filter.label"
					type="button"
					:class="[
						'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-150',
						category === filter.value
							? 'bg-white text-gray-900 shadow-xs font-semibold'
							: 'text-gray-600 hover:text-gray-900 hover:bg-white/50',
					]"
					@click="setCategory(filter.value)"
				>
					<component
						:is="filter.icon"
						v-if="filter.icon"
						class="h-3.5 w-3.5"
					/>
						<span>{{ filter.label }}</span>
					</button>
				</div>

				<label class="flex items-center gap-2 text-xs text-gray-500">
					<span class="sr-only sm:not-sr-only">Estatus</span>
					<select
						:value="status ?? ''"
						class="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-2xs transition-colors hover:bg-gray-50 focus:border-[#021F58] focus:outline-none focus:ring-2 focus:ring-[#021F58]/20"
						@change="setStatus((($event.target as HTMLSelectElement).value || null) as StatusFilter)"
					>
						<option
							v-for="option in statusFilters"
							:key="option.label"
							:value="option.value ?? ''"
						>
							{{ option.label }}
						</option>
					</select>
				</label>
			</div>
		</div>

		<!-- El deep link apunta a un reporte que ya no esta en la lista. Sin
		     esto, quien llega desde la campana se queda mirando una lista sin
		     ninguna senal de por que no pasa nada. -->
		<div
			v-if="targetIsMissing"
			class="rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-2.5"
			role="status"
		>
			<ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-amber-500" />
			<p class="text-xs text-amber-900 leading-relaxed">
				<template v-if="missingReason === 'filtered'">
					El reporte
					<span class="font-semibold">#{{ targetFeedbackId }}</span>
					no aparece con los filtros que tienes puestos. Quítalos para
					buscarlo.
				</template>
				<template v-else-if="missingReason === 'expired'">
					El reporte
					<span class="font-semibold">#{{ targetFeedbackId }}</span>
					ya cerró y dejó de mostrarse aquí. Los reportes cerrados se ven
					48 horas; lo que se resolvió sigue resuelto.
				</template>
				<template v-else-if="missingReason === 'not-found'">
					No encontramos el reporte
					<span class="font-semibold">#{{ targetFeedbackId }}</span>
					entre los tuyos.
				</template>
				<template v-else>
					El reporte
					<span class="font-semibold">#{{ targetFeedbackId }}</span>
					no está en esta página de la lista.
				</template>
			</p>
		</div>

		<!-- Skeleton Loading State -->
		<div
			v-if="isLoading"
			class="space-y-3"
		>
			<div
				v-for="n in 3"
				:key="n"
				class="p-5 rounded-2xl border border-gray-100 bg-white space-y-3 shadow-xs"
			>
				<div class="flex items-center justify-between">
					<Skeleton class="h-6 w-36 rounded-md" />
					<Skeleton class="h-4 w-28 rounded-md" />
				</div>
				<Skeleton class="h-12 w-full rounded-lg" />
			</div>
		</div>

		<!-- Error State: va antes del vacío. Un 500 o la red caída dejan isLoading en
		     false con la lista vacía, y sin esta rama la pantalla decía "sin reportes",
		     que es mentira y además no ofrece reintentar. -->
		<div
			v-else-if="isError"
			class="rounded-2xl border border-dashed border-red-200 bg-red-50/50 py-12 px-4 text-center"
		>
			<div class="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-xs border border-red-100 mb-3">
				<ExclamationTriangleIcon class="h-6 w-6 text-red-500" />
			</div>
			<h3 class="text-sm font-semibold text-gray-800">No pudimos cargar tus reportes</h3>
			<p class="mt-1 text-xs text-gray-500 max-w-sm mx-auto">
				Puede ser una falla de conexión. Vuelve a intentarlo.
			</p>
			<button
				type="button"
				class="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-2xs transition-colors hover:bg-gray-50 disabled:opacity-50"
				:disabled="isFetching"
				@click="refetch()"
			>
				<ArrowPathIcon
					class="h-3.5 w-3.5"
					:class="isFetching && 'animate-spin'"
				/>
				{{ isFetching ? 'Reintentando…' : 'Reintentar' }}
			</button>
		</div>

		<!-- Empty State -->
		<div
			v-else-if="feedbacks.length === 0"
			class="rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 py-12 px-4 text-center"
		>
			<div class="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-xs border border-gray-100 mb-3">
				<ChatBubbleLeftRightIcon class="h-6 w-6 text-gray-400" />
			</div>
			<h3 class="text-sm font-semibold text-gray-800">Sin reportes enviados</h3>
			<p class="mt-1 text-xs text-gray-500 max-w-sm mx-auto">
				Utiliza el botón flotante de feedback en la esquina inferior izquierda para reportar un problema o compartir sugerencias.
			</p>
		</div>

		<!-- Feedbacks List -->
		<ul
			v-else
			class="space-y-3.5"
			:class="isFetching && 'opacity-60 transition-opacity'"
		>
			<li
				v-for="item in feedbacks"
				:key="item.id"
				class="rounded-2xl border border-gray-200/80 bg-white p-5 space-y-3 transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
			>
				<!-- Card Header: Left Icon Avatar + Title + Route, Right Date.
				     En móvil la fecha baja: compitiendo por el ancho dejaba el título en
				     tres renglones y el badge partido a la mitad. -->
				<div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
					<div class="flex items-center gap-3 min-w-0">
						<div :class="['flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors shadow-2xs', categoryMeta[item.category].iconBg]">
							<component
								:is="categoryMeta[item.category].icon"
								class="h-5 w-5"
							/>
						</div>
						<div class="flex flex-col min-w-0">
							<div class="flex items-center gap-2 flex-wrap">
								<span class="text-sm font-semibold text-gray-900 leading-tight">
									{{ categoryMeta[item.category].title }}
								</span>
								<!-- whitespace-nowrap: "En proceso de solución" en un contenedor
								     angosto se partía en cuatro renglones y la píldora quedaba
								     hecha una bola. Que ocupe su renglón, no que se rompa. -->
								<span
									:class="[
										'inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-semibold border',
										familyBadge[item.statusFamily],
									]"
								>
									{{ item.statusLabel }}
								</span>
								<span class="inline-flex max-w-full items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 font-mono text-[11px] font-medium border border-gray-200/80 shadow-2xs">
									<GlobeAltIcon class="h-3 w-3 text-gray-500 shrink-0" />
									<span class="truncate">{{ item.route }}</span>
								</span>
							</div>
							<span class="text-[11px] text-gray-400 font-medium mt-0.5">
								Enviado por el usuario
							</span>
						</div>
					</div>

					<div class="flex items-center gap-1 text-gray-400 text-xs font-medium shrink-0 pt-0.5">
						<ClockIcon class="h-3.5 w-3.5" />
						<span>{{ formatDateTime(item.createdAt) }}</span>
					</div>
				</div>

				<!-- Comment Text: Indented & cleanly styled -->
				<p class="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-normal sm:pl-13">
					{{ item.comment }}
				</p>

				<!-- Resolución: lo que soporte le contesta al cerrar. Va destacada y
				     fuera del desplegable — es la respuesta a su reporte, no un detalle
				     que haya que ir a buscar. -->
				<div
					v-if="item.resolutionNote"
					class="sm:pl-13"
				>
					<div class="rounded-xl border border-gray-200/80 bg-gray-50/70 p-3.5">
						<p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
							Respuesta de soporte
						</p>
						<p class="mt-1 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
							{{ item.resolutionNote }}
						</p>
					</div>
				</div>

				<!-- Testimonial Tag Footer -->
				<div
					v-if="item.category === 'otro' && item.testimonialConsent"
					class="sm:pl-13 pt-1"
				>
					<span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/70">
						<CheckCircleIcon class="h-3.5 w-3.5 text-emerald-600" />
						Autorizado para testimonio público
					</span>
				</div>

				<!-- Seguimiento: la bitácora se pide al abrir, no con el listado. -->
				<div class="sm:pl-13">
					<button
						type="button"
						class="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 -ml-2 text-xs font-medium text-[#021F58] transition-colors hover:bg-blue-50/70"
						:aria-expanded="expandedId === item.id"
						:aria-controls="`feedback-timeline-${item.id}`"
						@click="toggle(item.id)"
					>
						<ChevronRightIcon
							class="h-3.5 w-3.5 transition-transform duration-150"
							:class="expandedId === item.id && 'rotate-90'"
						/>
						{{ expandedId === item.id ? 'Ocultar seguimiento' : 'Ver seguimiento' }}
					</button>

					<div
						v-if="expandedId === item.id"
						:id="`feedback-timeline-${item.id}`"
						class="mt-2 border-l-2 border-gray-100 pl-4 space-y-3"
					>
						<div
							v-if="isTimelineLoading"
							class="space-y-2"
						>
							<Skeleton class="h-4 w-48 rounded-md" />
							<Skeleton class="h-4 w-32 rounded-md" />
						</div>

						<!-- El 410 no es una falla: es un enlace de la campana que sobrevivió
						     al reporte. Merece su propio texto. -->
						<p
							v-else-if="isExpired"
							class="text-xs text-gray-500"
						>
							Este reporte ya cerró y dejó de mostrarse.
						</p>

						<p
							v-else-if="isTimelineError"
							class="text-xs text-gray-500"
						>
							No pudimos cargar el seguimiento de este reporte.
						</p>

						<p
							v-else-if="timeline.length === 0"
							class="text-xs text-gray-500"
						>
							Todavía no hay movimientos que mostrar.
						</p>

						<!-- El v-for va DENTRO del v-else, no en el mismo elemento: con las
						     dos directivas juntas el orden de evaluación queda sujeto a cómo
						     el compilador las precedencie. -->
						<template v-else>
							<div
								v-for="event in timeline"
								:key="event.id"
								class="relative"
							>
								<span
									class="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full"
									:class="event.isMine ? 'bg-gray-300' : 'bg-[#021F58]'"
								/>
								<div class="flex flex-wrap items-baseline gap-x-2">
									<span class="text-xs font-semibold text-gray-800">
										{{ event.statusLabel ?? event.author }}
									</span>
									<span class="text-[11px] text-gray-400">
										{{ formatDateTime(event.createdAt) }}
									</span>
								</div>
								<!-- Interpolación, no v-html: el cuerpo lo escriben soporte y el
								     propio usuario, así que se pinta como texto siempre. -->
								<p
									v-if="event.body"
									class="mt-0.5 text-xs text-gray-600 leading-relaxed whitespace-pre-wrap"
								>
									{{ event.body }}
								</p>
							</div>
						</template>

						<!-- Caja de respuesta. Sólo cuando el detalle cargó: sobre un
						     410 no hay reporte al que contestar, y sobre un error de red
						     el envío fallaría igual. -->
						<div
							v-if="!isTimelineLoading && !isExpired && !isTimelineError"
							class="pt-1"
						>
							<label
								:for="`feedback-reply-${item.id}`"
								class="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
							>
								Responder
							</label>
							<textarea
								:id="`feedback-reply-${item.id}`"
								v-model="replyBody"
								rows="3"
								:disabled="isReplying"
								class="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-2xs transition-colors placeholder:text-gray-400 focus:border-[#021F58] focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 disabled:opacity-60"
								placeholder="Agrega los pasos, una captura de lo que ves o cualquier detalle que ayude."
							/>

							<!-- Se avisa ANTES de escribir. Enterarse de que el reporte se
							     reabre después de mandar es enterarse tarde. -->
							<p
								v-if="item.isClosed"
								class="mt-1 text-[11px] text-gray-500"
							>
								Este reporte ya está cerrado: al responder lo reabrimos y
								soporte lo vuelve a revisar.
							</p>

							<button
								type="button"
								class="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#021F58] px-3 py-1.5 text-xs font-semibold text-white shadow-2xs transition-colors hover:bg-[#021F58]/90 disabled:opacity-40"
								:disabled="isReplying || !replyBody.trim()"
								@click="submitReply(item)"
							>
								<ArrowPathIcon
									v-if="isReplying"
									class="h-3.5 w-3.5 animate-spin"
								/>
								{{ isReplying ? 'Enviando…' : 'Enviar respuesta' }}
							</button>
						</div>
					</div>
				</div>
			</li>
		</ul>

		<!-- Pagination Bar -->
		<div
			v-if="lastPage > 1"
			class="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-100 pt-4 text-xs text-gray-500"
		>
			<span class="font-medium">
				Mostrando página {{ currentPage }} de {{ lastPage }} ({{ total }} reportes)
			</span>

			<div class="flex items-center gap-2">
				<button
					type="button"
					class="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white shadow-2xs transition-colors"
					:disabled="currentPage <= 1"
					@click="setPage(currentPage - 1)"
				>
					<ChevronLeftIcon class="h-3.5 w-3.5" />
					Anterior
				</button>
				<button
					type="button"
					class="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white shadow-2xs transition-colors"
					:disabled="currentPage >= lastPage"
					@click="setPage(currentPage + 1)"
				>
					Siguiente
					<ChevronRightIcon class="h-3.5 w-3.5" />
				</button>
			</div>
		</div>
	</div>
</template>

